import {
  Sun,
  CalendarDays,
  FileText,
  Compass,
  BarChart3,
  Clock,
  AlertTriangle,
  Lightbulb,
  Target,
  CheckCircle2,
  XCircle,
  PenLine,
  Check,
  ShieldCheck,
} from "lucide-react";
import { Sparkle } from "@/components/shared/Motifs";

function Divider() {
  return (
    <div className="my-4 flex items-center justify-center gap-2 text-gold/50" aria-hidden="true">
      <span className="h-px w-10 bg-gradient-to-r from-transparent to-gold/40" />
      <Sparkle size={8} className="text-gold" />
      <span className="h-px w-10 bg-gradient-to-l from-transparent to-gold/40" />
    </div>
  );
}

function SectionLabel({ icon: Icon, children, tone = "navy" }) {
  const tones = {
    navy: "text-navy",
    green: "text-emerald-700",
    amber: "text-amber-700",
  };
  return (
    <div className={`flex items-center gap-2 ${tones[tone]}`}>
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">{children}</span>
    </div>
  );
}

export function ReportContent({ report }) {
  const L = report.labels;
  return (
    <div className="px-4 py-5 text-navy" dir={report.dir || "ltr"}>
      {/* Header */}
      <div className="text-center">
        <p className="font-heading text-lg font-semibold">{report.greeting}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-navy/70">{report.intro}</p>
        <p className="mt-2 inline-block rounded-full bg-navy/[0.05] px-3 py-1 text-[11px] font-medium text-navy/70">
          {report.date}
        </p>
      </div>

      <Divider />

      {/* Overall outlook */}
      <SectionLabel icon={Sun} tone="green">{L.outlook}</SectionLabel>
      <div className="mt-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {report.overall.badge}
        </span>
        <p className="mt-2 text-[13px] leading-relaxed text-navy/80">{report.overall.body}</p>
      </div>

      <Divider />

      {/* Day type */}
      <SectionLabel icon={CalendarDays}>{L.dayType}</SectionLabel>
      <p className="mt-2 font-heading text-base font-semibold">{report.dayType.title}</p>
      {report.dayType.body && (
        <p className="mt-1 text-[13px] leading-relaxed text-navy/70">{report.dayType.body}</p>
      )}

      <Divider />

      {/* Summary */}
      <SectionLabel icon={FileText}>{L.summary}</SectionLabel>
      <p className="mt-2 text-[13px] leading-relaxed text-navy/80">{report.summary}</p>

      {report.direction && (
        <>
          <Divider />
          <SectionLabel icon={Compass}>{L.direction}</SectionLabel>
          <p className="mt-2 font-heading text-base font-semibold">{report.direction.title}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-navy/70">{report.direction.body}</p>
        </>
      )}

      <Divider />

      {/* Scores */}
      <SectionLabel icon={BarChart3}>{L.scores}</SectionLabel>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {report.scores.map((s) => (
          <div key={s.label} className="rounded-xl bg-cream/70 p-3 ring-1 ring-navy/5">
            <p className="text-[11px] font-medium leading-tight text-navy/60">{s.label}</p>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-heading text-lg font-semibold text-navy">{s.value}</span>
              <span className="text-[10px] text-navy/50">/ 10</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-navy/10">
              <div className="h-full rounded-full bg-gold" style={{ width: `${parseFloat(s.value) * 10}%` }} />
            </div>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-gold">{s.tag}</p>
          </div>
        ))}
      </div>

      <Divider />

      {/* Best window */}
      <div className="rounded-2xl bg-emerald-50/70 p-4 ring-1 ring-emerald-100">
        <SectionLabel icon={Clock} tone="green">{L.best}</SectionLabel>
        <p className="mt-2 font-heading text-base font-semibold text-emerald-800">{report.best.time}</p>
        <p className="mt-1 text-[12px] text-navy/70">{report.best.note}</p>
        <ul className="mt-2 space-y-1.5">
          {report.best.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-[13px] text-navy/80">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
              {it}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 rounded-2xl bg-amber-50/70 p-4 ring-1 ring-amber-100">
        <SectionLabel icon={AlertTriangle} tone="amber">{L.caution}</SectionLabel>
        <p className="mt-2 font-heading text-base font-semibold text-amber-800">{report.caution.time}</p>
        <p className="mt-1 text-[12px] text-navy/70">{report.caution.note}</p>
        <ul className="mt-2 space-y-1.5">
          {report.caution.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-[13px] text-navy/80">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
              {it}
            </li>
          ))}
        </ul>
      </div>

      <Divider />

      {/* Opportunity */}
      <SectionLabel icon={Lightbulb}>{L.opportunity}</SectionLabel>
      <p className="mt-2 text-[13px] leading-relaxed text-navy/80">{report.opportunity}</p>

      {report.focus && (
        <>
          <Divider />
          <SectionLabel icon={Target}>{L.focus}</SectionLabel>
          <p className="mt-2 text-[13px] leading-relaxed text-navy/80">{report.focus}</p>
        </>
      )}

      <Divider />

      {/* Favoured / Avoid */}
      <div className="grid grid-cols-1 gap-3">
        <div>
          <SectionLabel icon={CheckCircle2} tone="green">{L.favoured}</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.favoured.map((f) => (
              <span key={f} className="rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-medium text-emerald-700 ring-1 ring-emerald-100">
                {f}
              </span>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel icon={XCircle} tone="amber">{L.avoid}</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.avoid.map((f) => (
              <span key={f} className="rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-medium text-amber-700 ring-1 ring-amber-100">
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>

      <Divider />

      {/* Insight */}
      <div className="rounded-2xl bg-navy p-4 text-cream">
        <SectionLabel icon={PenLine} tone="navy">
          <span className="text-gold">{L.insight}</span>
        </SectionLabel>
        <p className="mt-2 text-[13px] leading-relaxed text-cream/85">{report.insight}</p>
      </div>

      {/* Outro */}
      <div className="mt-4 text-center">
        <p className="text-[12px] leading-relaxed text-navy/60">{report.outro}</p>
        <p className="mt-1 text-[13px] font-medium text-navy/80">{report.closing}</p>
        <div className="mt-3 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-gold" />
          <span className="font-heading text-sm font-semibold text-navy">ShubhSamay</span>
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Safalta Ka Sahi Samay</p>
      </div>
    </div>
  );
}

// Premium phone frame — WhatsApp-inspired, not a clone. The report is the hero.
export function PhoneFrame({ children, className = "" }) {
  return (
    <div className={`relative mx-auto w-full max-w-[340px] ${className}`}>
      <div className="relative rounded-[2.75rem] border border-navy/10 bg-navy p-2.5 shadow-[0_40px_80px_-24px_rgba(15,27,61,0.45)]">
        {/* side buttons */}
        <div className="absolute -left-[3px] top-24 h-14 w-[3px] rounded-l bg-navy/40" />
        <div className="absolute -right-[3px] top-20 h-10 w-[3px] rounded-r bg-navy/40" />
        <div className="overflow-hidden rounded-[2.25rem] bg-cream">
          {/* App header bar */}
          <div className="relative flex items-center gap-3 bg-navy px-4 pb-3 pt-3.5 text-cream">
            <div className="relative">
              <span className="absolute left-1/2 top-2 h-1 w-16 -translate-x-1/2 rounded-full bg-cream/15" />
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/30">
              <Sparkle size={16} className="text-gold" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-semibold leading-tight">ShubhSamay</p>
              <p className="flex items-center gap-1 text-[10px] text-cream/60">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Daily Guidance • 7:00 AM
              </p>
            </div>
          </div>
          {/* Scrollable report */}
          <div className="no-scrollbar h-[560px] overflow-y-auto bg-cream">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
